import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  extractErrorCode,
  isPhoneInUseError,
  resolveAcceptInviteErrorMessage,
} from "@/lib/errors/accept-invite-error";
import { Routes } from "@/lib/router";
import { history } from "@/lib/router/history";
import { logger } from "@/logger";

/**
 * Tien seconden in plaats van de gebruikelijke drie.
 *
 * Deze melding is geen bevestiging maar een instructie: vul het wachtwoord van je bestaande account
 * in. Wie hem niet uitleest staat stil, en dat is precies waarom #221 gemeld werd.
 */
const ACCOUNT_EXISTS_TOAST_DURATION = 10_000;

/**
 * Wat er op het scherm gebeurt als het accepteren van een uitnodiging misgaat.
 *
 * Drie uitkomsten, en het verschil zit in wat iemand daarna moet doen:
 *
 * - Het telefoonnummer hangt al aan een ander account: een venster, want de oplossing loopt via je
 *   manager en dat leest niemand in een toast weg.
 * - Het e-mailadres heeft al een Boomer-account: een toast met de weg naar "wachtwoord vergeten",
 *   want wie hier komt kent dat wachtwoord meestal niet meer.
 * - Al het andere: de vertaalde melding bij de code, of de generieke zin als we de code niet kennen.
 */
export function useAcceptInviteErrorHandler({ onPhoneInUse }: { onPhoneInUse: () => void }) {
  const { t } = useTranslation();

  return useCallback(
    (error: unknown) => {
      const code = extractErrorCode(error);
      logger.error("Accept invite error", { error, code });

      if (isPhoneInUseError(error)) {
        onPhoneInUse();
        return;
      }

      if (code === "ACCOUNT_EXISTS_PASSWORD_REQUIRED") {
        toast.error(resolveAcceptInviteErrorMessage(error, t), {
          duration: ACCOUNT_EXISTS_TOAST_DURATION,
          action: {
            label: t("actions:accept-invite.forgot-password"),
            onClick: () => history.push(Routes.AuthForgotPassword()),
          },
        });
        return;
      }

      toast.error(resolveAcceptInviteErrorMessage(error, t));
    },
    [t, onPhoneInUse],
  );
}
